// Silent two-slide hero rotator for Section 01 · THE BRAND.
// Crossfades between the peacock framed artwork (slide 1) and the 24KT Ganesha
// (slide 2) every 5s. Shares the exact coal + gold-haze + floor-reflection
// backdrop used by the Vitrine "clean" mode so both slides feel like a single
// museum-lit stage. No controls, no arrows, no dots — ambient movement only.
import { useEffect, useRef, useState } from "react";
import { Reveal } from "@/components/Reveal";
import { IS_EXPORT } from "@/lib/exportMode";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const cleanUrl = (src, threshold = 232, soft = 26) =>
  `${BACKEND_URL}/api/asset/clean-bg?url=${encodeURIComponent(src)}&threshold=${threshold}&soft=${soft}`;

const INTERVAL_MS = 5000;
const CROSSFADE_MS = 800;

export default function HeroRotator({
  slides,
  aspect = "aspect-[4/5] lg:aspect-[3/4]",
  delay = 0,
}) {
  const total = slides.length;
  const [index, setIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isTabHidden, setIsTabHidden] = useState(
    typeof document !== "undefined" ? document.hidden : false,
  );
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const rootRef = useRef(null);

  // Reduced-motion preference
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setPrefersReducedMotion(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  // Page Visibility API
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVis = () => setIsTabHidden(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // IntersectionObserver — only run while hero is on screen
  useEffect(() => {
    if (!rootRef.current || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => setIsVisible(e.isIntersecting)),
      { threshold: 0.25 },
    );
    obs.observe(rootRef.current);
    return () => obs.disconnect();
  }, []);

  // Autoplay
  useEffect(() => {
    if (IS_EXPORT || total < 2) return undefined;
    const shouldRun = isVisible && !isTabHidden && !prefersReducedMotion;
    if (!shouldRun) return undefined;
    const t = setInterval(() => setIndex((i) => (i + 1) % total), INTERVAL_MS);
    return () => clearInterval(t);
  }, [isVisible, isTabHidden, prefersReducedMotion, total]);

  // Only slide 1 in PDF export
  const visibleSlides = IS_EXPORT ? slides.slice(0, 1) : slides;

  return (
    <Reveal delay={delay} className="w-full">
      <div ref={rootRef} className={`relative ${aspect} w-full overflow-hidden`}>
        {/* Shared ambient backdrop — same layering as Vitrine "clean" mode */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 70% 60% at 50% 38%, #2A2218 0%, #1A140E 45%, #0E0A06 100%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 55% 40% at 50% 25%, rgba(200,164,106,0.28) 0%, rgba(200,164,106,0.06) 45%, transparent 75%)",
          }}
        />
        <div
          className="absolute inset-x-0 bottom-0 h-1/3"
          style={{
            background:
              "linear-gradient(180deg, transparent 0%, rgba(200,164,106,0.08) 50%, rgba(200,164,106,0.16) 100%)",
          }}
        />

        {/* Slides — absolute-positioned, opacity crossfade */}
        {visibleSlides.map((s, i) => {
          const isActive = i === index;
          const src = s.mode === "clean" ? cleanUrl(s.src, s.threshold, s.soft) : s.src;
          return (
            <div
              key={s.src}
              className="absolute inset-0 flex items-center justify-center"
              style={{
                opacity: isActive ? 1 : 0,
                transition: `opacity ${CROSSFADE_MS}ms ease-in-out`,
                pointerEvents: isActive ? "auto" : "none",
                zIndex: isActive ? 2 : 1,
              }}
              aria-label={s.ariaLabel}
              aria-hidden={!isActive}
              role="img"
            >
              <img
                src={src}
                alt={s.alt}
                crossOrigin="anonymous"
                loading="eager"
                fetchpriority={i === 0 ? "high" : "low"}
                className="h-full w-full object-contain p-2 md:p-3 lg:p-4"
                style={{
                  filter: s.filter,
                }}
              />
            </div>
          );
        })}

        {/* Gold hairline frame + corner accents */}
        <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-gold/30 z-10" />
        <div className="pointer-events-none absolute top-3 left-3 h-5 w-5 border-t border-l border-gold/60 z-10" />
        <div className="pointer-events-none absolute top-3 right-3 h-5 w-5 border-t border-r border-gold/60 z-10" />
        <div className="pointer-events-none absolute bottom-3 left-3 h-5 w-5 border-b border-l border-gold/60 z-10" />
        <div className="pointer-events-none absolute bottom-3 right-3 h-5 w-5 border-b border-r border-gold/60 z-10" />
      </div>
    </Reveal>
  );
}
